import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import UnifiedCheckout from '../components/UnifiedCheckout';
import {
  Sparkles,
  Brain,
  Scale,
  Calculator,
  Building2,
  Home,
  Wrench,
  MapPin,
  HeadphonesIcon,
  Target,
  Shield,
  MessageSquare,
  Mail,
  Phone,
  Instagram,
  CheckCircle2,
  ArrowRight,
  Crown,
  Zap,
  TrendingUp,
  BarChart3,
  AlertTriangle,
  Lightbulb,
  Users,
  Globe,
  Lock,
  Bot,
  Mic
} from 'lucide-react';

const UPIMIAPage = () => {
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const handleSubscribe = () => {
    if (!isAuthenticated) {
      toast({
        title: "Inicia sesión",
        description: "Debes iniciar sesión para suscribirte a UPIM IA",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }
    setCheckoutOpen(true);
  };

  const aiAgents = [
    {
      icon: Scale,
      name: "Agente Legal Inmobiliario MX",
      description: "Experto en contratos, régimen de condominio, RPP, notarías y normativa por estado",
      features: ["32 códigos civiles estatales", "Validación de contratos", "Alertas de riesgo legal"]
    },
    {
      icon: Calculator,
      name: "Agente Fiscal Inmobiliario MX",
      description: "Especialista en IVA, ISR, retenciones y facturación para operaciones inmobiliarias",
      features: ["Cálculo de impuestos", "Optimización fiscal", "Alertas de cumplimiento"]
    },
    {
      icon: Building2,
      name: "Agente Industrial / Nearshoring",
      description: "Experto en naves industriales, bodegas y oportunidades de nearshoring",
      features: ["Análisis de mercado", "Due diligence", "Tendencias sectoriales"]
    },
    {
      icon: Home,
      name: "Agente Residencial y Vivienda",
      description: "Especialista en mercado residencial, valuación y tendencias de vivienda",
      features: ["Comparables de mercado", "Análisis de plusvalía", "Perfiles de comprador"]
    },
    {
      icon: Wrench,
      name: "Agente Técnico por Tipo de Inmueble",
      description: "Asesoría técnica específica según el tipo de propiedad",
      features: ["Especificaciones técnicas", "Normativas de construcción", "Inspecciones"]
    },
    {
      icon: MapPin,
      name: "Agente Regional por Estado",
      description: "Conocimiento específico de cada estado: leyes, mercado y prácticas locales",
      features: ["32 estados de México", "Código civil estatal", "Prácticas regionales"]
    },
    {
      icon: HeadphonesIcon,
      name: "Agente de Atención Premium",
      description: "Lenguaje profesional, protocolos de servicio y manejo de objeciones",
      features: ["Respuestas sugeridas", "Manejo de quejas", "Escalamiento inteligente"]
    },
    {
      icon: Target,
      name: "Agente de Ventas y Cierre",
      description: "Argumentación, seguimiento estratégico y técnicas de cierre ético",
      features: ["Siguiente mejor acción", "Objeciones comunes", "Scripts de venta"]
    },
    {
      icon: Shield,
      name: "Agente de Ética UPIM",
      description: "Cumplimiento del código de honor y estándares profesionales UPIM",
      features: ["Validación ética", "Alertas de cumplimiento", "Mejores prácticas"]
    }
  ];

  const benefits = [
    {
      icon: Brain,
      title: "IA equivalente a Salesforce Einstein",
      description: "Predicción de cierre, scoring de leads, riesgo de abandono"
    },
    {
      icon: Scale,
      title: "Agentes IA especializados en leyes mexicanas",
      description: "Conocimiento de los 32 códigos civiles estatales"
    },
    {
      icon: Lightbulb,
      title: "Recomendaciones de siguiente mejor acción",
      description: "IA prescriptiva para cerrar más operaciones"
    },
    {
      icon: AlertTriangle,
      title: "Análisis de riesgo legal y fiscal",
      description: "Alertas en tiempo real antes de cerrar operaciones"
    },
    {
      icon: MessageSquare,
      title: "Omnicanal con IA",
      description: "WhatsApp, Facebook, Instagram, Email con respuestas sugeridas"
    },
    {
      icon: Users,
      title: "Respuestas profesionales para clientes",
      description: "Lenguaje corporativo adaptado al sector inmobiliario"
    },
    {
      icon: Zap,
      title: "Automatización inteligente",
      description: "Seguimiento automático y nurturing de leads"
    },
    {
      icon: Crown,
      title: "Estándares premium UPIM",
      description: "Respaldo institucional y supervisión de calidad"
    }
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Hero */}
        <div className="bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djJIMjR2LTJoMTJ6bTAtNHYySDI0di0yaDEyem0wLTR2Mkgy';'NHYtMmgxMnptMC00djJIMjR2LTJoMTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative">
            <div className="text-center max-w-4xl mx-auto">
              <Badge className="bg-yellow-500 text-yellow-900 mb-6 text-sm px-4 py-1">
                <Sparkles className="h-4 w-4 mr-2" />
                Add-On Premium
              </Badge>
              
              <h1 className="text-4xl lg:text-6xl font-bold mb-6">
                UPIM IA
              </h1>
              <p className="text-2xl lg:text-3xl text-blue-200 mb-4">
                Altitud Inmobiliaria
              </p>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                El equivalente funcional de <strong>Salesforce + Einstein</strong> para el sector inmobiliario mexicano, con gobierno institucional y estándares profesionales superiores.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="bg-white text-gray-900 hover:bg-gray-100 h-14 px-8 text-lg"
                  onClick={handleSubscribe}
                  data-testid="subscribe-upim-ia"
                >
                  <Crown className="h-5 w-5 mr-2" />
                  Activar UPIM IA - $1,000 MXN/mes
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white/30 text-white hover:bg-white/10 h-14 px-8"
                  onClick={() => document.getElementById('agents').scrollIntoView({ behavior: 'smooth' })}
                >
                  Ver Agentes IA
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </div>

              <p className="text-sm text-gray-400 mt-6">
                El CRM base está incluido en tu afiliación UPIM. UPIM IA es un servicio adicional premium.
              </p>
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Beneficios de UPIM IA
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              IA profesional de nivel corporativo, adaptada a México
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="h-14 w-14 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-7 w-7 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-sm text-gray-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* AI Agents Section */}
        <div id="agents" className="bg-gray-100 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-purple-100 text-purple-700">9 Agentes Especializados</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Agentes IA Tropicalizados a México
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Cada agente está entrenado con normativa mexicana, conocimiento regional y mejores prácticas del sector
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {aiAgents.map((agent, index) => {
                const Icon = agent.icon;
                return (
                  <Card key={index} className="hover:shadow-lg transition-all hover:-translate-y-1">
                    <CardHeader>
                      <div className="flex items-start gap-4">
                        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{agent.name}</CardTitle>
                          <CardDescription className="mt-1">{agent.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {agent.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                            <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>

        {/* IA Capabilities */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-100 text-blue-700">Capacidades IA</Badge>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Inteligencia Artificial de Nivel Enterprise
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">IA Predictiva</h3>
                    <p className="text-gray-600 text-sm mt-1">
                      Probabilidad de cierre por oportunidad, detección de leads prioritarios, riesgo de abandono del cliente
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">IA Prescriptiva</h3>
                    <p className="text-gray-600 text-sm mt-1">
                      Recomendaciones de siguiente acción, alertas de seguimiento, advertencias legales/fiscales
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Bot className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">IA Asistencial Multi-Agente</h3>
                    <p className="text-gray-600 text-sm mt-1">
                      Chat contextual dentro del CRM, asistencia en ventas, atención al cliente y asesoría técnica
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Mic className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Preparado para Voz</h3>
                    <p className="text-gray-600 text-sm mt-1">
                      Arquitectura lista para speech-to-text y text-to-speech (próximamente)
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6">Omnicanal con IA</h3>
              <p className="text-gray-300 mb-8">
                Bandeja unificada que concentra todos tus canales de comunicación con respuestas sugeridas por IA
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 text-green-400" />
                  <span className="text-sm">WhatsApp</span>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <svg className="h-8 w-8 mx-auto mb-2 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  <span className="text-sm">Facebook</span>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <Instagram className="h-8 w-8 mx-auto mb-2 text-pink-400" />
                  <span className="text-sm">Instagram</span>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <Mail className="h-8 w-8 mx-auto mb-2 text-red-400" />
                  <span className="text-sm">Email</span>
                </div>
              </div>

              <div className="mt-6 p-4 bg-white/5 rounded-xl">
                <p className="text-sm text-gray-300">
                  <strong className="text-white">Funciones:</strong> Respuestas sugeridas, clasificación automática de intención, registro automático en CRM, escalamiento humano
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Potencia tu CRM con Inteligencia Artificial
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Únete a los profesionales inmobiliarios que ya están cerrando más operaciones con UPIM IA
            </p>
            <Button 
              size="lg" 
              className="bg-white text-blue-600 hover:bg-blue-50 h-14 px-10 text-lg"
              onClick={handleSubscribe}
            >
              <Crown className="h-5 w-5 mr-2" />
              Activar UPIM IA - $1,000 MXN/mes
            </Button>
            <p className="text-sm text-blue-200 mt-4">
              Cancela cuando quieras. Sin compromisos a largo plazo.
            </p>
          </div>
        </div>

        {/* Checkout Modal */}
        <UnifiedCheckout
          isOpen={checkoutOpen}
          onClose={() => setCheckoutOpen(false)}
          product={{
            id: 'upim-ia-subscription',
            type: 'subscription',
            name: 'UPIM IA – Altitud Inmobiliaria',
            description: 'Suscripción mensual a UPIM IA con todos los agentes especializados',
            price: 1000,
            features: [
              'IA equivalente a Salesforce Einstein',
              '9 Agentes IA especializados',
              'Conocimiento de 32 estados de México',
              'Omnicanal con IA (WhatsApp, FB, IG, Email)',
              'Soporte prioritario UPIM'
            ]
          }}
          onSuccess={(result) => {
            toast({
              title: "¡Bienvenido a UPIM IA!",
              description: "Tu suscripción ha sido activada"
            });
            setCheckoutOpen(false);
            navigate('/crm');
          }}
          successUrl={`${window.location.origin}/upim-ia/bienvenido`}
          cancelUrl={`${window.location.origin}/upim-ia`}
        />
      </div>
    </Layout>
  );
};

export default UPIMIAPage;
